import type { Freigabebefund, Textbaustein, Textwert } from './typen'
import { gibtText, satz, t } from './sprache'
import type { Sprachschluessel } from './sprache'

/**
 * **Eine Meldung sagt, was zu tun ist, und führt dorthin** (7.1 Abschnitt 13,
 * R1527–R1530).
 *
 * Hier steht, wohin jeder Befund der Freigabeprüfung führt und mit welcher
 * Auswahl — an einer Stelle, weil Arbeitsliste und Konzernabschluss-Monitor dieselben Sätze
 * zeigen und zwei Tabellen derselben Ziele auseinanderliefen.
 */

/** Wohin ein Verweis führt: der Ort und, wo die Seite sie übernimmt, die Auswahl (R1528). */
export type Sprungziel = {
  bereich: string
  unterpunkt?: string
  /** Was die Zielseite beim Öffnen vorbereitet — etwa `rechnen=KAB-2`. */
  auswahl?: Record<string, string>
}

/** Was eine Meldung über ihre Lage weiß, ohne dass der Rechenkern es schickt. */
export type Meldungskontext = {
  /**
   * Der Konzernabschluss, um den es geht — sein Schlüssel für den Verweis und
   * sein Name für die Überschrift (R1527). Ohne Konzernabschluss steht der
   * Name des Kreises da und kein Schlüssel.
   */
  abschluss?: { schluessel: string | null; name: string } | null
}

const MONITOR: Sprungziel = { bereich: 'abschluss', unterpunkt: 'monitor' }
const KFR: Sprungziel = { bereich: 'berichte', unterpunkt: 'kapitalflussrechnung' }
const IC_ABSTIMMUNG: Sprungziel = { bereich: 'abschluss', unterpunkt: 'konzernabschluesse' }
const BETEILIGUNGSREGISTER: Sprungziel = { bereich: 'konzernstruktur', unterpunkt: 'beteiligungsregister' }

/** Der Weg zu den Konzernabschlüssen — mit der Rückfrage „Konzernabschluss rechnen" offen, wo es ihn gibt. */
export function zumRechnen(kontext: Meldungskontext): Sprungziel {
  const schluessel = kontext.abschluss?.schluessel
  return schluessel
    ? { bereich: 'abschluss', unterpunkt: 'konzernabschluesse', auswahl: { rechnen: schluessel } }
    : { bereich: 'abschluss', unterpunkt: 'konzernabschluesse' }
}

/**
 * Wohin ein Befund führt (R1528, Tabelle in 7.1 Abschnitt 13).
 *
 * Die Spiegelprüfungen führen in ihren Spiegel; Rückstellungs- und
 * Sonderpostenspiegel haben noch keinen Bericht und führen in den Konzernabschluss-Monitor.
 */
export function verweisJeBefund(code: string, kontext: Meldungskontext): Sprungziel {
  switch (code) {
    case 'veraltet-regelwerk':
    case 'veraltet-daten':
    case 'veraltet-vorperiode':
    case 'veraltet-frueherer-monat':
      return zumRechnen(kontext)
    case 'bilanz-geht-nicht-auf':
      return { bereich: 'berichte', unterpunkt: 'bilanz' }
    case 'anhang-unvollstaendig':
      return { bereich: 'anhang', unterpunkt: 'vollstaendigkeit' }
    case 'anhang-wahlrechte':
      return { bereich: 'anhang', unterpunkt: 'anhangsbuch' }
    case 'ohne-lieferung':
      return { bereich: 'datenimport', unterpunkt: 'lieferungen' }
    case 'ic-ohne-partner':
    case 'beteiligung-ohne-partner':
    case 'ic-differenz-nicht-geklaert':
      return IC_ABSTIMMUNG
    case 'kfr-geht-nicht-auf':
    case 'kfr-umgliederung-nicht-null':
      return KFR
    case 'spiegel-anlagen':
      return { bereich: 'berichte', unterpunkt: 'spiegel' }
    case 'spiegel-eigenkapital':
      return { bereich: 'berichte', unterpunkt: 'eigenkapitalspiegel' }
    case 'spiegel-verbindlichkeiten':
      return { bereich: 'berichte', unterpunkt: 'kreditspiegel' }
    case 'kurse-aktualisieren':
      return { bereich: 'stammdaten', unterpunkt: 'kurse' }
    case 'eintrittskurs-nachtragen':
      return { bereich: 'konzernstruktur', unterpunkt: 'kreise' }
    case 'einzahlung-passt-nicht-zur-quote':
    case 'zukauf-von-anteilen':
      return BETEILIGUNGSREGISTER
    case 'zwischenergebnis-ohne-schritt':
      return { bereich: 'abschluss', unterpunkt: 'konzernabschluesse' }
    case 'ergebnisjahr-im-eigenkapital':
      return { bereich: 'stammdaten', unterpunkt: 'historische-kurse' }
    default:
      return MONITOR
  }
}

/**
 * **Die Befunde, deren Zielseite die Auswahl noch nicht übernimmt** (R1530).
 *
 * Ihr Verweis öffnet die richtige Seite, aber noch nicht an der Stelle. Gebaut
 * wird Seite für Seite; die Liste darf nur kürzer werden, und ihr Testfall
 * meldet jeden Befund, der dazukommt.
 */
export const OHNE_AUSWAHL: readonly string[] = [
  'bilanz-geht-nicht-auf',
  'anhang-unvollstaendig',
  'anhang-wahlrechte',
  'ohne-lieferung',
  'ic-ohne-partner',
  'beteiligung-ohne-partner',
  'ic-differenz-nicht-geklaert',
  'kfr-geht-nicht-auf',
  'kfr-umgliederung-nicht-null',
  'spiegel-anlagen',
  'spiegel-eigenkapital',
  'spiegel-sonderposten',
  'spiegel-rueckstellungen',
  'spiegel-verbindlichkeiten',
  'kurse-aktualisieren',
  'eintrittskurs-nachtragen',
  'einzahlung-passt-nicht-zur-quote',
  'zukauf-von-anteilen',
  'zwischenergebnis-ohne-schritt',
  'ergebnisjahr-im-eigenkapital',
]

/** Der Name der Seite, auf die ein Verweis führt — so, wie die Leiste ihn nennt. */
export function seitenname(ziel: Sprungziel): string {
  return ziel.unterpunkt
    ? t(`bereich.${ziel.bereich}.${ziel.unterpunkt}` as Sprachschluessel)
    : t(`bereich.${ziel.bereich}` as Sprachschluessel)
}

/** Ein Baustein mit den Werten, die nur die Anzeige kennt: Name des Abschlusses, Name der Zielseite. */
export function mitLage(baustein: Textbaustein, zusatz: Textwert[]): Textbaustein {
  const vorhanden = new Set(baustein.werte.map((w) => w.feld))
  return { ...baustein, werte: [...baustein.werte, ...zusatz.filter((z) => !vorhanden.has(z.feld))] }
}

/**
 * **Die Einzahl, wo es sie gibt:** Die Anzahl steht in der Überschrift
 * (R1527), und *„1 Beträge … haben"* ist kein Satz. Trägt ein Befund die
 * Anzahl 1 und gibt es zu seinem Schlüssel eine Fassung `….eins`, steht sie da.
 */
export function inZahl(baustein: Textbaustein): Textbaustein {
  const anzahl = baustein.werte.find((w) => w.feld === 'anzahl')?.wert
  const eins = `${baustein.schluessel}.eins`
  return anzahl === '1' && gibtText(eins) ? { ...baustein, schluessel: eins } : baustein
}

/**
 * Überschrift, Satz mit Verweis und Ziel eines Befunds (R1527, R1528).
 *
 * Der Satz darunter bekommt die Werte der Überschrift mit: *„Klappen Sie im
 * [[{seite}]] …"* braucht den Spiegel, den die Überschrift nennt.
 */
export function befundtexte(
  b: Freigabebefund,
  kontext: Meldungskontext,
): { was: string; weg: string | null; ziel: Sprungziel } {
  const ziel = verweisJeBefund(b.code, kontext)
  const zusatz: Textwert[] = [
    { feld: 'abschluss', art: 'text', wert: kontext.abschluss?.name ?? '' },
    { feld: 'seite', art: 'text', wert: seitenname(ziel) },
  ]
  const meldung = inZahl(mitLage(b.meldung, zusatz))

  return {
    was: satz(meldung),
    weg: b.handreichung ? satz(mitLage(b.handreichung, meldung.werte), undefined, true) : null,
    ziel,
  }
}
